import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface PageTitleState {
  title: string;
  setTitle: (title: string) => void;
}

const PageTitleContext = createContext<PageTitleState | null>(null);

const DEFAULT_TITLE = 'Inicio';

/**
 * The topbar shows the title of the current screen, and only the screen knows
 * it — a project page shows the project name, which is not in the URL. Pages
 * publish their title through `usePageTitle`.
 */
export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const value = useMemo(() => ({ title, setTitle }), [title]);
  return <PageTitleContext.Provider value={value}>{children}</PageTitleContext.Provider>;
}

function usePageTitleContext(): PageTitleState {
  const context = useContext(PageTitleContext);
  if (!context) throw new Error('usePageTitle must be used inside a PageTitleProvider');
  return context;
}

/** Read the current title (used by the topbar). */
export function useCurrentPageTitle(): string {
  return usePageTitleContext().title;
}

/** Publish the title of the current screen, and mirror it to document.title. */
export function usePageTitle(title: string | undefined): void {
  const { setTitle } = usePageTitleContext();

  useEffect(() => {
    if (!title) return;
    setTitle(title);
    document.title = `${title} · KainOS`;
    return () => {
      document.title = 'KainOS';
    };
  }, [title, setTitle]);
}
